<template>
<div class="recipe-detail-full" v-if="recipe">
  <CancelLeftoversModal @remove-leftovers="onRemoveLeftovers" />
  <LeftoversModal :adjustmentDelta="recipe.isYieldFixed ? recipe.serves : 1" @add-leftovers="onAddLeftovers" />

  <div class="recipe-title-wrapper">
    <h2 class="recipe-title">
      {{ recipe.title }}
      <small v-if="hasMacros">
        {{ $t('lblCalories') }} {{ formattedMacro(recipe, 'calories').unit }} • {{ $t('lblCarbs') }} {{ formattedMacro(recipe, 'carbs').unit }} •
        {{ $t('lblProtien') }} {{ formattedMacro(recipe, 'protein').unit }} • {{ $t('lblFat') }} {{ formattedMacro(recipe, 'fat').unit }}
      </small>
    </h2>
    <BaseButton type="button" class="btn-small btn-clear btn-close" @click="setRecipeDetailFull(null)">
      {{ $t('lblClose') }}
    </BaseButton>
  </div>

  <div class="detail-content-container">
    <div class="detail-content-wrapper">
      <Hero :imageUrl="recipe.imageURL" />

      <div class="leftover-mealtime" v-if="recipe.isLeftover">
        <h4>{{ $t('recipes.lblLeftoversfrom') }} {{ recipe.preparationDate }}</h4>
      </div>

      <div class="serves-select-wrapper" v-if="data.isPlanned">
        <div class="serves-select">
          <span @click="decrementServings">
            <i class="fas fa-minus serves-select-icon" />
          </span>
          <div class="adjust-serves">
            {{ $t('recipes.lblServes') }} <strong>{{ recipe.scaledIngredients[servingsIndex].serves }}</strong>
          </div>
          <span @click="incrementServings">
            <i class="fas fa-plus serves-select-icon" />
          </span>
        </div>
      </div>

      <div v-if="!recipe.isLeftover">
        <div v-if="recipe.hasLeftovers">
          <p class="leftover-amount"> {{ leftoverServingText }} </p>
          <div class="recipe-action-link cancel" @click="$bvModal.show('cancel-leftovers-modal')" v-if="recipe.leftoverServings > 0">
            {{ $t('lblCancel') }}
          </div>
        </div>
      </div>

      <div class="recipe-action-link remove" @click="removeRecipe" v-if="(data.isPlanned && !data.parentMealId) || (recipe.isLeftover && recipe.dishId)">
        {{ $t('recipes.lblRemoveFromPlan') }}
      </div>

      <div v-if="recipe.description" class="detail-section description">
        <h3>{{ $t('recipes.lblDescription') }}</h3>
        <div class="description-copy">
          <p>{{ recipe.description }}</p>
        </div>
      </div>

      <div class="recipe-yield-wrapper" v-if="recipe.servingSizeText">
        <strong>Serving Size:</strong> <span>{{ recipe.servingSizeText }}</span>
      </div>

      <div class="detail-section ingredients" v-if="!recipe.isLeftover">
        <h3>{{ $t('recipes.lblIngredients') }}</h3>
        <div
          v-if="recipeScaledIngredients"
          class="ingredient-item"
          v-for="(ingredient, index) in recipeScaledIngredients.ingredients"
          :key="'ingredient' + index"
        >
          <div class="ingredient">
            {{ `${ingredient.count}${ingredient.metric ? ' ' + ingredient.metric : ''}` }}
            {{
              `${' ' + ingredient.ingredientName}${ingredient.prepInstruction && ingredient.prepInstruction !== '' ? ' - ' + ingredient.prepInstruction : ''}`
            }}
          </div>
        </div>
      </div>
    </div>

    <div class="detail-content-wrapper">
      <div class="instructions" v-if="!recipe.isLeftover">
        <h3>{{ $t('recipes.lblInstructionsList') }}</h3>
        <div class="instruction-item" v-for="(instruction, index) in recipe.instructions" :key="'instruction' + index">
          <div class="instruction">
            {{ `${instruction.step}.` }}
            {{ `${instruction.description}` }}
          </div>
        </div>
      </div>

      <NutritionTable title="Nutritional Information" :elements="getNutrition"></NutritionTable>
    </div>
  </div>
</div>
</template>

<script>
import store from '@state/store';
import { kitchenDrawerComputed, kitchenDrawerMethods, planMethods, dishesMethods, recipesMethods, groceryListMethods } from '@state/helpers';
import utils from '@src/utils/utils';
import { macrosMixin } from 'macrosMixin';
import NutritionTable from '../Recipes/NutritionTable';
import Hero from '../Hero/Hero';
import LeftoversModal from '../Modals/LeftoversModal';
import CancelLeftoversModal from '../Modals/CancelLeftoversModal';

export default {
  mixins: [macrosMixin],
  components: {
    CancelLeftoversModal,
    Hero,
    LeftoversModal,
    NutritionTable,
  },
  props: {
    isEmbed: {
      type: Boolean,
    },
    data: {
      type: Object,
    },
    onTabletUp: {
      type: Boolean,
    },
  },
  data() {
    return {
      adjustedServes: 0,
    };
  },
  computed: {
    ...kitchenDrawerComputed,
    recipe() {
      return this.data;
    },
    getNutrition() {
      return this.data.nutritionDigest || [];
    },
    leftoversServingsIndexAdjustment() {
      const leftoverServings = this.recipe.leftoverServings > 0 ? this.recipe.leftoverServings : 0;
      return this.recipe.isYieldFixed ? leftoverServings / this.recipe.serves : leftoverServings;
    },
    leftoverServingText() {
      return this.recipe.leftoverServings > 0
        ? `${this.recipe.leftoverServings} ${this.$t('lblExtraServingsforLeftovers')}`
        : this.$t('recipes.lblUsingforLeftovers');
    },
    servingsIndex() {
      return this.recipe.defaultScaledIngredientIndex + this.adjustedServes;
    },
    hasMacros() {
      return (this.recipe.aggregatedNutritionDigest && Object.keys(this.recipe.aggregatedNutritionDigest.macroNutritionDigest).length) ||
        Object.keys(this.recipe.macroNutritionDigest).length
        ? true
        : false;
    },
    recipeScaledIngredients() {
      return this.servingsIndex ? this.recipe.scaledIngredients[this.servingsIndex + this.leftoversServingsIndexAdjustment] : {};
    },
  },
  mounted() {},
  methods: {
    ...dishesMethods,
    ...kitchenDrawerMethods,
    ...planMethods,
    ...recipesMethods,
    ...groceryListMethods,
    addRecipe(recipe) {
      this.setDishToAdd({ mealId: null, recipeId: recipe.id });
      this.keepNavStateButToggleDrawer();
    },
    async removeRecipe() {
      const result = await this.removeDish(this.recipe.dishId);
      if (result) {
        this.data.onRemove(this.recipe.dishId);
        this.getCurrentPlanGrid().finally(() => {
          if (this.onTabletUp === undefined || this.onTabletUp) {
            return this.getGroceryList();
          }
        });
        this.navigateHigher();
      }
    },
    incrementServings() {
      if (this.servingsIndex + this.leftoversServingsIndexAdjustment < this.recipe.scaledIngredients.length - 1) {
        this.adjustedServes += 1;
        this.adjustServings(this, this.recipe.scaledIngredients[this.servingsIndex].serves);
      }
    },
    decrementServings() {
      if (this.servingsIndex > 0) {
        this.adjustedServes -= 1;
        this.adjustServings(this, this.recipe.scaledIngredients[this.servingsIndex].serves);
      }
    },
    async onAddLeftovers(extraServings) {
      const response = await this.adjustLeftovers({
        id: this.recipe.dishId,
        payload: {
          leftoverServings: extraServings,
          mealId: this.recipe.parentMealId || null,
          recipeId: this.recipe.id || null,
        },
      });

      if (response.dishType !== 'm') {
        this.$emit('drawerComponentDataChanged', response);
      } else {
        const updatedRecipe = response.recipes.find((recipe) => (recipe.id = this.data.id));
        this.$emit('drawerComponentDataChanged', updatedRecipe);
      }
      this.getCurrentPlanGrid().finally(() => {
        if (this.onTabletUp === undefined || this.onTabletUp) {
          return this.getGroceryList();
        }
      });
    },
    async onRemoveLeftovers() {
      const response = await this.adjustLeftovers({
        id: this.recipe.dishId,
        payload: {
          leftoverServings: 0,
          mealId: this.recipe.parentMealId || null,
          recipeId: this.recipe.id || null,
        },
      });

      if (response.dishType !== 'm') {
        this.$emit('drawerComponentDataChanged', response);
      } else {
        const updatedRecipe = response.recipes.find((recipe) => (recipe.id = this.data.id));
        this.$emit('drawerComponentDataChanged', updatedRecipe);
      }
      this.getCurrentPlanGrid().finally(() => {
        if (this.onTabletUp === undefined || this.onTabletUp) {
          return this.getGroceryList();
        }
      });
    },
    adjustServings: _.debounce(async (vm, servings) => {
      const payload = {
        serves: servings,
        mealId: vm.data.parentMealId || null,
        recipeId: vm.data.id,
      };
      const response = await vm.reviseServes({ dishId: vm.data.dishId, payload });

      vm.adjustedServes = 0;

      // update the dish within PlanSummary Component... this is not the reactive update of this component!
      vm.data.onRevision(response);

      if (response.dishType !== 'm') {
        // emit a change event so that KitchenDrawer can update THIS components data object.
        vm.$emit('drawerComponentDataChanged', response);
      } else {
        const updatedRecipe = response.recipes.find((recipe) => (recipe.id = vm.data.id));
        // emit a change event so that KitchenDrawer can update THIS components data object.
        vm.$emit('drawerComponentDataChanged', updatedRecipe);
      }
    }, 1000),
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.recipe-detail-full {
  @extend %scrollable-drawer-content;

  background-color: $color-recipe-detail-bg;
  padding: 3rem;
  z-index: 9999;

  .recipe-title-wrapper {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;

    .recipe-title {
      color: $color-meal-card-horizontal-title;
      padding: 0 1.6rem;

      small {
        color: $color-meal-card-horizontal-subtitle;
        display: block;
        font-size: 70%;
      }
    }

    .btn-close {
      margin-top: 0;
    }
  }

  .detail-content-container {
    display: flex;
    padding: 0 1.6rem 10rem;

    .detail-content-wrapper {
      flex: 1;

      &:first-child {
        margin-right: 2rem;
      }
    }

    .btn-secondary {
      margin-bottom: 2rem;
    }

    .detail-section {
      margin-bottom: 3.2rem;
    }

    .section-title {
      color: $color-darkGrey;
      line-height: 2.175rem;
      margin-bottom: 2rem;

      small {
        color: $color-meal-card-horizontal-subtitle;
        display: block;
        font-size: 75%;
      }
    }

    .leftover-mealtime {
      align-items: center;
      background-color: $color-empty-mealtime-slot-bg;
      border: 1px solid $base-border-color;
      border-radius: $base-border-radius;
      display: flex;
      height: 8.3rem;
      justify-content: center;
      margin-bottom: 2.4rem;
      padding: 1.7rem;
      text-align: center;

      h4 {
        margin-bottom: 0;
      }
    }

    .description {
      h3 {
        color: $color-grey;
        font-weight: 600;
        margin-bottom: 1.5rem;
      }

      .description-copy {
        color: $color-lightGrey;
        font-size: 1.6rem;
        line-height: 2rem;
        margin-bottom: 1.5rem;
      }
    }

    .recipe-yield-wrapper {
      font-size: 1.77rem;
      line-height: 1.4;
      margin-bottom: 3.2rem;

      strong {
        color: $color-grey;
        font-weight: 600;
        margin-right: 0.5rem;
      }

      span {
        color: $color-lightGrey;
        font-size: 1.6rem;
      }

      p {
        color: lighten($color-lightGrey, 20);
        font-size: 1.4rem;
      }
    }

    .instructions {
      h3 {
        color: $color-grey;
        font-weight: 600;
        margin-bottom: 1.5rem;
      }

      .instruction-item {
        margin-top: 0;
        text-align: left;

        .instruction {
          color: $color-lightGrey;
          font-size: 1.4rem;
          letter-spacing: 0.35px;
          line-height: 1.9rem;
          margin-bottom: 2rem;
        }
      }
    }

    .ingredients {
      h3 {
        color: $color-grey;
        font-weight: 600;
        margin-bottom: 1.5rem;
      }

      .ingredient-item {
        color: $color-lightGrey;
        font-size: 1.6rem;
        line-height: 2rem;
        margin-bottom: 2rem;
      }
    }
  }

  .leftover-amount {
    font-size: 1.3rem;
    font-weight: 900;
    text-align: center;
  }

  .recipe-action-link {
    color: $color-action;
    font-size: 1.6rem;
    font-weight: bold;
    letter-spacing: 0.15rem;
    margin-bottom: 3rem;
    text-align: center;

    &.remove {
      color: #dc3545;
    }

    &:hover {
      cursor: pointer;
    }

    &.cancel {
      font-size: 1.4rem;
      margin-bottom: 2.4rem;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .serves-select-wrapper {
    display: flex;
    flex: 2;
    justify-content: center;
    margin-bottom: 1.3rem;
    padding: 0.5rem 8.5rem;

    .serves-select {
      align-items: center;
      border: 1px solid $base-border-color;
      border-radius: $base-border-radius;
      display: flex;
      flex: 1;
      font-size: 1.3rem;
      justify-content: space-between;
      max-width: 17.3rem;
      padding: 1.3rem 2rem;

      .adjust-serves {
        font-size: 1.5rem;
        margin-bottom: 0;
      }

      .serves-select-icon {
        color: $color-action;
        font-size: 1.4rem;
      }
    }
  }
}
</style>
